<template>
  <div class="user-stats">
    <div class="page-header">
      <h2>用户统计</h2>
      <div class="header-actions">
        <el-button @click="loadStats" :loading="loading">刷新数据</el-button>
      </div>
    </div>

    <!-- 总体统计卡片 -->
    <div class="stats-overview">
      <el-card class="stat-card">
        <div class="stat-content">
          <div class="stat-number">{{ stats.total_users }}</div>
          <div class="stat-label">总用户数</div>
          <div class="stat-trend">
            <el-icon class="trend-icon up"><ArrowUp /></el-icon>
            <span>+{{ stats.new_users_week }} 本周</span>
          </div>
        </div>
        <div class="stat-icon total">
          <el-icon><User /></el-icon>
        </div>
      </el-card>

      <el-card class="stat-card">
        <div class="stat-content">
          <div class="stat-number">{{ stats.active_users }}</div>
          <div class="stat-label">活跃用户</div>
          <div class="stat-trend">
            <el-icon class="trend-icon up"><ArrowUp /></el-icon>
            <span>{{ ((stats.active_users / stats.total_users) * 100).toFixed(1) }}%</span>
          </div>
        </div>
        <div class="stat-icon active">
          <el-icon><Check /></el-icon>
        </div>
      </el-card>

      <el-card class="stat-card">
        <div class="stat-content">
          <div class="stat-number">{{ stats.online_users }}</div>
          <div class="stat-label">在线用户</div>
          <div class="stat-trend">
            <el-icon class="trend-icon up"><ArrowUp /></el-icon>
            <span>实时数据</span>
          </div>
        </div>
        <div class="stat-icon online">
          <el-icon><Connection /></el-icon>
        </div>
      </el-card>

      <el-card class="stat-card">
        <div class="stat-content">
          <div class="stat-number">{{ stats.new_users_today }}</div>
          <div class="stat-label">今日新增</div>
          <div class="stat-trend">
            <el-icon class="trend-icon up"><ArrowUp /></el-icon>
            <span>较昨日</span>
          </div>
        </div>
        <div class="stat-icon new">
          <el-icon><Plus /></el-icon>
        </div>
      </el-card>
    </div>

    <!-- 详细统计图表 -->
    <div class="stats-charts">
      <div class="chart-row">
        <!-- 设备类型分布 -->
        <el-card class="chart-card">
          <template #header>
            <div class="card-header">
              <span>设备类型分布</span>
              <el-tag>{{ Object.values(stats.device_types).reduce((a, b) => a + b, 0) }} 台设备</el-tag>
            </div>
          </template>
          <div class="device-stats">
            <div 
              v-for="(count, type) in stats.device_types" 
              :key="type"
              class="device-item"
            >
              <div class="device-info">
                <div class="device-icon" :class="type.toLowerCase()">
                  <el-icon v-if="type === 'Windows'"><Monitor /></el-icon>
                  <el-icon v-else-if="type === 'Mac'"><Monitor /></el-icon>
                  <el-icon v-else-if="type === 'Linux'"><Monitor /></el-icon>
                  <el-icon v-else><Phone /></el-icon>
                </div>
                <div class="device-details">
                  <div class="device-name">{{ type }}</div>
                  <div class="device-count">{{ count }} 台</div>
                </div>
              </div>
              <div class="device-progress">
                <el-progress 
                  :percentage="(count / Object.values(stats.device_types).reduce((a, b) => a + b, 0) * 100)"
                  :show-text="false"
                  :stroke-width="8"
                />
              </div>
            </div>
          </div>
        </el-card>

        <!-- 卡密类型分布 -->
        <el-card class="chart-card">
          <template #header>
            <div class="card-header">
              <span>卡密类型分布</span>
              <el-tag type="success">{{ Object.values(stats.card_types).reduce((a, b) => a + b, 0) }} 张卡密</el-tag>
            </div>
          </template>
          <div class="card-stats">
            <div 
              v-for="(count, type) in stats.card_types" 
              :key="type"
              class="card-item"
            >
              <div class="card-info">
                <el-tag :type="getCardTypeColor(type)" size="small">
                  {{ getCardTypeName(type) }}
                </el-tag>
                <div class="card-count">{{ count }} 张</div>
              </div>
              <div class="card-progress">
                <el-progress 
                  :percentage="(count / Object.values(stats.card_types).reduce((a, b) => a + b, 0) * 100)"
                  :show-text="false"
                  :stroke-width="6"
                  :color="getProgressColor(type)"
                />
              </div>
            </div>
          </div>
        </el-card>
      </div>

      <div class="chart-row">
        <!-- 地理位置分布 -->
        <el-card class="chart-card full-width">
          <template #header>
            <div class="card-header">
              <span>用户地理分布</span>
              <el-tag type="info">Top 5 城市</el-tag>
            </div>
          </template>
          <div class="location-stats">
            <div 
              v-for="(count, location) in stats.locations" 
              :key="location"
              class="location-item"
            >
              <div class="location-info">
                <div class="location-name">{{ location }}</div>
                <div class="location-count">{{ count }} 用户</div>
              </div>
              <div class="location-progress">
                <el-progress 
                  :percentage="(count / Object.values(stats.locations).reduce((a, b) => a + b, 0) * 100)"
                  :stroke-width="12"
                  :color="getLocationColor(location)"
                />
              </div>
            </div>
          </div>
        </el-card>
      </div>
    </div>

    <!-- 用户状态分布 -->
    <div class="status-overview">
      <el-card>
        <template #header>
          <span>用户状态分布</span>
        </template>
        <div class="status-stats">
          <div class="status-item">
            <div class="status-circle active"></div>
            <div class="status-info">
              <div class="status-label">活跃用户</div>
              <div class="status-count">{{ stats.active_users }}</div>
            </div>
          </div>
          <div class="status-item">
            <div class="status-circle banned"></div>
            <div class="status-info">
              <div class="status-label">封禁用户</div>
              <div class="status-count">{{ stats.banned_users }}</div>
            </div>
          </div>
          <div class="status-item">
            <div class="status-circle inactive"></div>
            <div class="status-info">
              <div class="status-label">非活跃用户</div>
              <div class="status-count">{{ stats.total_users - stats.active_users - stats.banned_users }}</div>
            </div>
          </div>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { User, Check, Connection, Plus, ArrowUp, Monitor, Phone } from '@element-plus/icons-vue'
import { userAPI } from '@/services/api'

export default {
  name: 'UserStats',
  components: {
    User,
    Check,
    Connection,
    Plus,
    ArrowUp,
    Monitor,
    Phone
  },
  setup() {
    const loading = ref(false)
    
    const stats = reactive({
      total_users: 0,
      active_users: 0,
      banned_users: 0,
      online_users: 0,
      new_users_today: 0,
      new_users_week: 0,
      device_types: {},
      card_types: {},
      locations: {}
    })

    // 加载统计数据
    const loadStats = async () => {
      try {
        loading.value = true
        const response = await userAPI.getUserStats()
        
        if (response.success) {
          Object.assign(stats, response.data)
        } else {
          ElMessage.error(response.message || '获取统计数据失败')
        }
      } catch (error) {
        ElMessage.error('获取统计数据失败: ' + error.message)
      } finally {
        loading.value = false
      }
    }

    // 获取卡密类型名称
    const getCardTypeName = (type) => {
      const names = {
        'AI_HOST_BASIC': 'AI主机基础版',
        'AI_HOST_PRO': 'AI主机专业版',
        'AI_BOX_BASIC': 'AI盒子基础版',
        'AI_BOX_PRO': 'AI盒子专业版',
        'ALL_IN_ONE': '全功能版'
      }
      return names[type] || type
    }

    // 获取卡密类型颜色
    const getCardTypeColor = (type) => {
      const colors = {
        'AI_HOST_BASIC': '',
        'AI_HOST_PRO': 'success',
        'AI_BOX_BASIC': 'info',
        'AI_BOX_PRO': 'warning',
        'ALL_IN_ONE': 'danger'
      }
      return colors[type] || ''
    }

    // 获取进度条颜色
    const getProgressColor = (type) => {
      const colors = {
        'AI_HOST_BASIC': '#909399',
        'AI_HOST_PRO': '#67c23a',
        'AI_BOX_BASIC': '#409eff',
        'AI_BOX_PRO': '#e6a23c',
        'ALL_IN_ONE': '#f56c6c'
      }
      return colors[type] || '#909399'
    }

    // 获取地理位置颜色
    const getLocationColor = (location) => {
      const colors = ['#409eff', '#67c23a', '#e6a23c', '#f56c6c', '#909399']
      const locations = Object.keys(stats.locations)
      const index = locations.indexOf(location)
      return colors[index % colors.length]
    }

    onMounted(() => {
      loadStats()
    })

    return {
      loading,
      stats,
      loadStats,
      getCardTypeName,
      getCardTypeColor,
      getProgressColor,
      getLocationColor
    }
  }
}
</script>

<style scoped>
.user-stats {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.stats-overview {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.stat-card {
  border-radius: 8px;
  overflow: hidden;
}

.stat-card :deep(.el-card__body) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
}

.stat-content {
  flex: 1;
}

.stat-number {
  font-size: 32px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 8px;
}

.stat-label {
  font-size: 14px;
  color: #909399;
  margin-bottom: 8px;
}

.stat-trend {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #67c23a;
}

.trend-icon {
  margin-right: 4px;
}

.trend-icon.up {
  color: #67c23a;
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  color: white;
}

.stat-icon.total {
  background: linear-gradient(135deg, #667eea, #764ba2);
}

.stat-icon.active {
  background: linear-gradient(135deg, #67c23a, #85ce61);
}

.stat-icon.online {
  background: linear-gradient(135deg, #409eff, #66b1ff);
}

.stat-icon.new {
  background: linear-gradient(135deg, #e6a23c, #ebb563);
}

.stats-charts {
  margin-bottom: 30px;
}

.chart-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.chart-row:last-child {
  margin-bottom: 0;
}

.chart-card {
  border-radius: 8px;
}

.chart-card.full-width {
  grid-column: 1 / -1;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.device-stats,
.card-stats {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.device-item,
.card-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.device-info,
.card-info {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 120px;
}

.device-icon {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.device-icon.windows {
  background: #0078d4;
}

.device-icon.mac {
  background: #000000;
}

.device-icon.linux {
  background: #fcc624;
}

.device-icon.android,
.device-icon.ios {
  background: #34a853;
}

.device-details {
  display: flex;
  flex-direction: column;
}

.device-name {
  font-weight: 500;
  font-size: 14px;
}

.device-count,
.card-count {
  font-size: 12px;
  color: #909399;
}

.device-progress,
.card-progress {
  flex: 1;
  margin-left: 20px;
}

.location-stats {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.location-item {
  display: flex;
  align-items: center;
  gap: 20px;
}

.location-info {
  min-width: 100px;
}

.location-name {
  font-weight: 500;
  margin-bottom: 4px;
}

.location-count {
  font-size: 12px;
  color: #909399;
}

.location-progress {
  flex: 1;
}

.status-overview {
  margin-bottom: 20px;
}

.status-stats {
  display: flex;
  justify-content: space-around;
  align-items: center;
}

.status-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.status-circle {
  width: 16px;
  height: 16px;
  border-radius: 50%;
}

.status-circle.active {
  background: #67c23a;
}

.status-circle.banned {
  background: #f56c6c;
}

.status-circle.inactive {
  background: #909399;
}

.status-info {
  text-align: center;
}

.status-label {
  font-size: 14px;
  color: #909399;
  margin-bottom: 4px;
}

.status-count {
  font-size: 18px;
  font-weight: bold;
  color: #303133;
}
</style>
